import { E2EE_IMAGE_MIMES, encryptedAttachmentSchema, type E2eeImageMime, type EncryptedAttachment, type Media } from "@atrium/shared";
import { rgbaToThumbHash, thumbHashToDataURL } from "thumbhash";
import { uploadTo } from "../api";
import { decryptFile, encryptFile, fromB64u, toB64u } from "./crypto";

/*
 * Images in end-to-end encrypted conversations. Discord's attachment pipeline,
 * moved onto the device because the server can't see inside:
 *
 * - The sender's device does what the media server would: applies EXIF
 *   orientation, strips metadata (location, camera) by re-encoding, and caps
 *   the size at 2048px. GIFs are kept as they are so they still animate.
 * - Like Discord, every image gets its dimensions and a ThumbHash placeholder,
 *   so the message lays out at its final size and shows a blurred preview
 *   while the real file downloads.
 * - The file is encrypted under its own random key and uploaded as soon as
 *   it's picked, so sending is instant. The key, hash and placeholder are only
 *   ever sent inside the message ciphertext.
 */

const MAX_SIDE = 2048;
const THUMB_SIDE = 100;
const isImageMime = (t: string): t is E2eeImageMime => (E2EE_IMAGE_MIMES as readonly string[]).includes(t);

interface Prepared {
  bytes: Uint8Array<ArrayBuffer>;
  mime: E2eeImageMime;
  width: number;
  height: number;
  placeholder: string;
  filename: string;
}

function canvas(w: number, h: number) {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

async function encode(c: OffscreenCanvas | HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  if ("convertToBlob" in c) return c.convertToBlob({ type, quality });
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't encode the image"))), type, quality));
}

/** ThumbHash wants at most 100×100 pixels of RGBA. */
function placeholderOf(bitmap: ImageBitmap): string {
  const scale = Math.min(1, THUMB_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const c = canvas(w, h);
  const ctx = c.getContext("2d") as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return toB64u(rgbaToThumbHash(w, h, ctx.getImageData(0, 0, w, h).data));
}

const renamed = (name: string, mime: string) => {
  const base = name.replace(/\.[^.]+$/, "").slice(0, 150) || "image";
  return `${base}.${mime === "image/jpeg" ? "jpg" : mime.split("/")[1]}`;
};

/** Normalise an image before encryption, as the server does for plaintext uploads. */
export async function prepareImage(file: File): Promise<Prepared> {
  if (!isImageMime(file.type)) throw new Error("Only images can be attached here");
  // Applies EXIF orientation, so the re-encoded pixels are upright without the metadata.
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }).catch(() => {
    throw new Error("That image couldn't be read");
  });
  try {
    const placeholder = placeholderOf(bitmap);
    if (file.type === "image/gif") {
      // Re-encoding would flatten the animation; GIFs carry no EXIF block.
      return { bytes: new Uint8Array(await file.arrayBuffer()), mime: "image/gif", width: bitmap.width, height: bitmap.height, placeholder, filename: renamed(file.name, "image/gif") };
    }
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const c = canvas(width, height);
    const ctx = c.getContext("2d") as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
    // WebP where the browser can write it (Safari can't, and silently gives PNG); keep PNG for PNGs, JPEG otherwise.
    let blob = await encode(c, "image/webp", 0.9);
    if (blob.type !== "image/webp") blob = file.type === "image/png" ? await encode(c, "image/png") : await encode(c, "image/jpeg", 0.9);
    const mime = isImageMime(blob.type) ? blob.type : "image/png";
    return { bytes: new Uint8Array(await blob.arrayBuffer()), mime, width, height, placeholder, filename: renamed(file.name, mime) };
  } finally {
    bitmap.close();
  }
}

/* ───── Sending ───── */

/** Descriptors for uploads that haven't been sent yet, by media id. Memory only: they hold file keys. */
const pending = new Map<string, EncryptedAttachment>();

/** Prepare, encrypt and upload one image. Same contract as uploadFile, for useUploads. */
export async function uploadEncryptedImage(file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Media> {
  const img = await prepareImage(file);
  const sealed = await encryptFile(img.bytes);
  const media = await uploadTo("/api/media/encrypted", new Blob([sealed.data], { type: "application/octet-stream" }), "encrypted.bin", onProgress, signal);
  const descriptor: EncryptedAttachment = {
    mediaId: media.id,
    key: sealed.key,
    iv: sealed.iv,
    sha256: sealed.sha256,
    mime: img.mime,
    size: img.bytes.byteLength,
    width: img.width,
    height: img.height,
    filename: img.filename,
    placeholder: img.placeholder,
  };
  pending.set(media.id, descriptor);
  // The sender already has the plaintext: show it without a round trip.
  remember(media.id, Promise.resolve(URL.createObjectURL(new Blob([img.bytes], { type: img.mime }))));
  return encryptedMedia(descriptor, media.url);
}

/** Descriptors for these uploads (in order), or null if any isn't an encrypted upload from this tab. */
export function takeDescriptors(mediaIds: string[]): EncryptedAttachment[] | null {
  const out = mediaIds.map((id) => pending.get(id));
  if (out.some((d) => !d)) return null;
  for (const id of mediaIds) pending.delete(id);
  return out as EncryptedAttachment[];
}

export const isEncryptedUpload = (mediaId: string) => pending.has(mediaId);

/* ───── Receiving ───── */

/** What the chat renders for a decrypted attachment: a normal image Media, with the descriptor attached. */
export function encryptedMedia(d: EncryptedAttachment, url: string): Media {
  return { id: d.mediaId, kind: "image", url, mime: d.mime, width: d.width, height: d.height, size: d.size, filename: d.filename ?? null, animated: d.mime === "image/gif", e2ee: d };
}

/** Attachment descriptors from a decrypted body, checked like any other untrusted input. */
export function safeAttachments(list: unknown): EncryptedAttachment[] {
  if (!Array.isArray(list)) return [];
  return list.slice(0, 10).flatMap((a) => {
    const p = encryptedAttachmentSchema.safeParse(a);
    return p.success ? [p.data] : [];
  });
}

export function placeholderUrl(d: EncryptedAttachment): string | null {
  if (!d.placeholder) return null;
  try {
    return thumbHashToDataURL(fromB64u(d.placeholder));
  } catch {
    return null;
  }
}

/** Decrypted object URLs by media id. Bounded; the oldest are revoked first. */
const urls = new Map<string, Promise<string>>();
const MAX_CACHED = 200;

function remember(id: string, p: Promise<string>) {
  urls.set(id, p);
  p.catch(() => urls.delete(id));
  while (urls.size > MAX_CACHED) {
    const [oldest, url] = urls.entries().next().value!;
    urls.delete(oldest);
    void url.then((u) => URL.revokeObjectURL(u)).catch(() => {});
  }
}

/**
 * Download, check and decrypt an encrypted image, as an object URL typed with
 * the mime from the signed message (never anything the server says).
 */
export function decryptedUrl(m: Media): Promise<string> {
  const d = m.e2ee;
  if (!d) return Promise.resolve(m.url);
  const hit = urls.get(d.mediaId);
  if (hit) return hit;
  const p = (async () => {
    const res = await fetch(m.url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("The image couldn't be downloaded");
    const data = await res.arrayBuffer();
    // GCM adds 16 bytes; anything else isn't the file the message describes.
    if (data.byteLength !== d.size + 16) throw new Error("The image doesn't match its message");
    const plain = await decryptFile(data, d);
    return URL.createObjectURL(new Blob([plain], { type: d.mime }));
  })();
  remember(d.mediaId, p);
  return p;
}
